import { useEffect, useRef, useState } from 'react';
import { DYNAMIC_FEATURE_TOGGLES } from './types';

type Mixpanel = {
  flags: {
    flags: Map<string, unknown>;
    get_variant_value_sync: (key: string) => string | undefined;
  };
};

declare global {
  interface Window {
    mixpanelFeatureFlags?: ((mixpanel: Mixpanel) => void)[];
  }
}

/**
 * Flags that the bridge does not read when Mixpanel loads them. Reading a
 * variant sends the `$experiment_started` exposure, so a test that applies to
 * only some pages reads its flag with `useMixpanelFlag` when it applies.
 */
const LAZY_MIXPANEL_FLAGS: readonly string[] = ['hideAppInstallBanner'];

const isDynamicFeatureToggle = (key: string) =>
  (DYNAMIC_FEATURE_TOGGLES as readonly string[]).includes(key);

let loadedMixpanel: Mixpanel | undefined;
const listeners = new Set<() => void>();

/**
 * The GTM tag `Mixpanel - Feature Flag - Bootstrap` calls every callback in
 * `window.mixpanelFeatureFlags` when Mixpanel loads or updates the flags. A flag
 * whose key is a dynamic feature toggle sets that toggle: on for any variant
 * except `control`. A lazy flag does not set its toggle; see `useMixpanelFlag`.
 */
export const registerMixpanelFeatureFlags = () => {
  window.mixpanelFeatureFlags = window.mixpanelFeatureFlags || [];
  window.mixpanelFeatureFlags.push(mixpanel => {
    mixpanel.flags.flags.forEach((_flag, key) => {
      if (!isDynamicFeatureToggle(key) || LAZY_MIXPANEL_FLAGS.includes(key)) {
        return;
      }

      // Reading the variant through Mixpanel sends the $experiment_started event.
      const variant = mixpanel.flags.get_variant_value_sync(key);
      window.featureToggles.push([
        key,
        variant !== undefined && variant !== 'control',
      ]);
    });

    loadedMixpanel = mixpanel;
    listeners.forEach(listener => listener());
  });
};

type MixpanelFlagState =
  | { status: 'loading' }
  /** Mixpanel loaded its flags, and this flag is not one of them. */
  | { status: 'missing' }
  | { status: 'assigned'; variant: string };

const LOADING: MixpanelFlagState = { status: 'loading' };

/**
 * Reads a lazy Mixpanel flag. The hook reads the variant, and with it sends the
 * exposure, only while `shouldRead` is `true`, and only once per page.
 *
 * @param key A key from `LAZY_MIXPANEL_FLAGS`.
 * @param shouldRead `true` when the test applies to the user on this page.
 */
export const useMixpanelFlag = (
  key: string,
  shouldRead: boolean,
): MixpanelFlagState => {
  const [mixpanel, setMixpanel] = useState(loadedMixpanel);
  const [state, setState] = useState<MixpanelFlagState>(LOADING);
  const isRead = useRef(false);

  useEffect(() => {
    const listener = () => setMixpanel(loadedMixpanel);
    listeners.add(listener);
    listener();

    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!mixpanel || !shouldRead || isRead.current) {
      return;
    }

    isRead.current = true;
    if (!mixpanel.flags.flags.has(key)) {
      setState({ status: 'missing' });
      return;
    }

    const variant = mixpanel.flags.get_variant_value_sync(key);
    setState(
      variant === undefined
        ? { status: 'missing' }
        : { status: 'assigned', variant },
    );
  }, [key, mixpanel, shouldRead]);

  return state;
};

/** Only for tests: forgets the Mixpanel instance of an earlier test. */
export const resetMixpanelFeatureFlags = () => {
  loadedMixpanel = undefined;
  listeners.clear();
};
